import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Badge, Box, styled, Typography } from '@mui/material';

import { useTheme } from '../styles/styles';

type TabOptionBase = {
  label: string;
  showIndicator?: boolean;
};

type RouteTabsProps =
  | {
      firstTabAsDefault?: boolean;
      updateUrlOnChange?: true;
      options: Array<TabOptionBase & { to: string }>;
      matchRouteKey: string;
      searchParams?: string;
      onClick?: (tab: string) => void;
      showIndicatorOnActiveTab?: boolean;
    }
  | {
      firstTabAsDefault?: boolean;
      updateUrlOnChange: false;
      options: Array<TabOptionBase & { key: string }>;
      matchRouteKey: string;
      searchParams?: string;
      onClick?: (tab: string) => void;
      showIndicatorOnActiveTab?: boolean;
    };

export const RouteTabs = memo((props: RouteTabsProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    matchRouteKey,
    options,
    onClick,
    searchParams = '',
    showIndicatorOnActiveTab = false,
    firstTabAsDefault = false,
  } = props;

  const updateUrlOnChange = props.updateUrlOnChange !== false;

  const suffix = useMemo(
    () => (searchParams ? `?${searchParams}` : ''),
    [searchParams]
  );

  const setActiveTab = useCallback(
    (value: string) => {
      if (updateUrlOnChange) navigate(value + suffix);
      onClick?.(value);
    },
    [navigate, suffix, updateUrlOnChange, onClick]
  );

  useEffect(() => {
    if (!options?.length || !firstTabAsDefault || !updateUrlOnChange) return;
    const typedOptions = options as Array<TabOptionBase & { to: string }>;
    const tabPaths = typedOptions
      .map((item) => item.to)
      .filter((p): p is string => Boolean(p));

    const hasTabInPath = tabPaths.some((tabPath) =>
      location.pathname.includes(`/${tabPath}`)
    );

    if (!hasTabInPath) {
      const firstWithPath = typedOptions.find((item) => item.to);

      if (firstWithPath) {
        navigate(firstWithPath.to + `?${searchParams}`);
        if (onClick) onClick(firstWithPath.to);
      }
    }
  }, [location.pathname, options, onClick]);
  const theme = useTheme();

  const activeTabRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });
  }, [matchRouteKey]);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '8px',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {options.map((item, index) => {
        const tabValue = updateUrlOnChange
          ? (item as any).to
          : (item as any).key;

        const isActive = matchRouteKey === tabValue;

        const shouldShowIndicator =
          Boolean(item.showIndicator) &&
          (showIndicatorOnActiveTab || !isActive);

        return (
          <Box
            key={index}
            ref={isActive ? activeTabRef : undefined}
            onClick={() => setActiveTab(tabValue)}
            sx={{
              flexShrink: 0,
              whiteSpace: 'nowrap',
              borderRadius: '10px',
              backgroundColor: isActive ? theme.colors.primary.main : 'white',
              padding: '10px 24px 9px 24px',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: isActive
                  ? undefined
                  : theme.background.lightGrey,
              },
            }}
          >
            {shouldShowIndicator ? (
              <Badge
                variant="dot"
                color="primary"
                overlap="circular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                  '& .MuiBadge-badge': {
                    top: 6,
                    right: -6,
                  },
                }}
              >
                <Label isActive={isActive}>{item.label}</Label>
              </Badge>
            ) : (
              <Label isActive={isActive}>{item.label}</Label>
            )}
          </Box>
        );
      })}
    </Box>
  );
});

const Label = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  cursor: 'pointer',
  color: isActive ? 'white' : theme.text.inactive,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  letterSpacing: '-0.15px',
}));
